/**
 * Matters Page
 *
 * CRUD interface for managing matters under a selected client.
 */

import React, { useState } from 'react';
import { Card, Stack, Flex, Button, Input, Modal, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { useMatters, useMatterMutations } from '../../api/matters';
import { useClients } from '../../api/clients';
import { useHierarchy } from '../../context/HierarchyContext';
import { LoadingState } from '../shared/LoadingState';
import { ErrorDisplay } from '../shared/ErrorDisplay';
import { ConfirmModal } from '../shared/ConfirmModal';
import type { Matter } from '../../api/types';
import { RoleGate } from '../shared/RoleGate';
import { GATES } from '../../auth/roles';
import { errorMessage } from '../../api/errors';

function MatterFormModal({ isOpen, onClose, onSave, initialName, initialCode, title }: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, code: string) => Promise<void>;
  initialName: string;
  initialCode: string;
  title: string;
}) {
  const { theme } = useTheme();
  const [name, setName] = useState(initialName);
  const [code, setCode] = useState(initialCode);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: '4px',
    border: `1px solid ${borderColor}`,
    background: theme.colors.background.paper,
    color: textColor,
    fontSize: fontSize.sm,
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setErrorMsg('');
    try {
      await onSave(name.trim(), code.trim());
    } catch (err: any) {
      setErrorMsg(err?.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header><h3 style={{ margin: 0, ...textStyles.label, color: textColor }}>{title}</h3></Modal.Header>
      <Modal.Body>
        <Stack gap="sm">
          <div>
            <label style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block', marginBottom: '4px' }}>Matter Name</label>
            <input placeholder="Matter name" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block', marginBottom: '4px' }}>Matter Code</label>
            <input placeholder="Matter code" value={code} onChange={(e) => setCode(e.target.value)} style={inputStyle} />
          </div>
          {errorMsg && <span style={{ color: theme.colors.semantic.error.default, fontSize: fontSize.xs }}>{errorMsg}</span>}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Flex gap="sm" justify="end">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="solid" color="primary" size="sm" onClick={handleSave} disabled={!name.trim() || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
}

export function MattersPage() {
  const { theme } = useTheme();
  const { selectedClient, selectMatter, bumpData } = useHierarchy();
  const [showAll, setShowAll] = useState(false);

  // When "show all" is on (or no client is selected), fetch every matter;
  // otherwise scope to the selected client.
  const scopeToClient = !!selectedClient && !showAll;
  const { data: matters, loading, error, refetch } = useMatters(
    scopeToClient ? { underClientId: selectedClient.id, limit: 1000 } : { limit: 1000 }
  );
  const { createMatter, updateMatter, deleteMatter } = useMatterMutations();

  // Client code lookup for the "show all" view.
  const { data: clients } = useClients({ limit: 1000 });
  const clientCode = React.useCallback(
    (idClient: number) => clients?.find((c) => c.id === idClient)?.clientcode || '—',
    [clients]
  );

  // In "show all", surface matters under the selected client first.
  const orderedMatters = React.useMemo(() => {
    if (!matters) return matters;
    if (!showAll || !selectedClient) return matters;
    const inCtx = matters.filter((m) => m.id_client === selectedClient.id);
    const rest = matters.filter((m) => m.id_client !== selectedClient.id);
    return [...inCtx, ...rest];
  }, [matters, showAll, selectedClient]);

  const [search, setSearch] = useState('');
  const filteredMatters = React.useMemo(() => {
    if (!orderedMatters) return orderedMatters;
    const q = search.trim().toLowerCase();
    if (!q) return orderedMatters;
    return orderedMatters.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.mattercode?.toLowerCase().includes(q) ||
        clientCode(m.id_client).toLowerCase().includes(q)
    );
  }, [orderedMatters, search, clientCode]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMatter, setEditingMatter] = useState<Matter | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Matter | null>(null);
  const [deleteBlockedMsg, setDeleteBlockedMsg] = useState('');

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  const handleSaveNew = async (name: string, code: string) => {
    if (!selectedClient) return;
    const created = await createMatter({ name, mattercode: code, id_client: selectedClient.id });
    setShowAddModal(false);
    // Select the new matter into context immediately so it's usable without a
    // refresh (the context bar renders a fallback option for it).
    if (created) selectMatter(created);
    refetch();
    bumpData();
  };

  const handleSaveEdit = async (name: string, code: string) => {
    if (!editingMatter) return;
    await updateMatter(editingMatter.id, { name, mattercode: code });
    setEditingMatter(null);
    refetch();
    bumpData();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMatter(deleteTarget.id);
      setDeleteTarget(null);
      refetch();
    bumpData();
    } catch (err: unknown) {
      // The service explains its own refusal — naming how many children block the
      // delete — so show that rather than a guess. The hardcoded text below is a
      // fallback only: it happens to be right for the common case, which is exactly
      // what makes substituting it dangerous. A 409 raised for any other reason
      // would be reported as a child-record problem the user cannot find.
      setDeleteBlockedMsg(
        errorMessage(err, 'Cannot delete: this matter has projects. Remove all projects first.')
      );
    }
  };

  if (!selectedClient && !showAll) {
    return (
      <Stack gap="lg">
        <h1 style={{ ...textStyles.h3, color: textColor }}>Matters</h1>
        <Card variant="outlined">
          <Card.Body>
            <Flex direction="column" align="center" gap="sm" style={{ padding: spacing.lg }}>
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>
                Select a client from the context bar above to view matters.
              </span>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: mutedColor, fontSize: fontSize.sm, cursor: 'pointer' }}>
                <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                Show all matters
              </label>
            </Flex>
          </Card.Body>
        </Card>
      </Stack>
    );
  }

  if (loading) return <LoadingState message="Loading matters..." />;
  if (error) return <ErrorDisplay error={error} onRetry={refetch} />;

  return (
    <Stack gap="lg">
      <Flex justify="between" align="center">
        <div>
          <h1 style={{ ...textStyles.h3, color: textColor }}>Matters</h1>
          <p style={{ margin: 0, marginTop: spacing.xs, fontSize: fontSize.sm, color: mutedColor }}>
            {showAll ? 'All matters' : `Under: ${selectedClient?.name}`}
          </p>
        </div>
        <Flex gap="md" align="center">
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: mutedColor, fontSize: fontSize.sm, cursor: 'pointer' }}>
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            Show all
          </label>
          <RoleGate minRole={GATES.hierarchyWrite}>
            <Button variant="solid" color="primary" size="sm" disabled={!selectedClient} onClick={() => setShowAddModal(true)}>+ Add Matter</Button>
          </RoleGate>
        </Flex>
      </Flex>

      <Input
        placeholder="Search matters by name or code..."
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
      />

      <Card variant="elevated">
        <Card.Body>
          {filteredMatters && filteredMatters.length > 0 ? (
            <div style={{ border: `1px solid ${borderColor}`, borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: showAll ? '1fr 90px 120px 150px 100px' : '1fr 90px 150px 100px',
                padding: `${spacing.sm} ${spacing.md}`,
                background: theme.colors.background.paper,
                borderBottom: `1px solid ${borderColor}`,
                fontSize: fontSize.xs,
                fontWeight: 600,
                color: mutedColor,
                textTransform: 'uppercase' as const,
              }}>
                <span>Name</span>
                <span>id_matter</span>
                {showAll && <span>Client Code</span>}
                <span>Code</span>
                <span style={{ textAlign: 'right' }}>Actions</span>
              </div>
              {filteredMatters.map((matter) => (
                <div
                  key={matter.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: showAll ? '1fr 90px 120px 150px 100px' : '1fr 90px 150px 100px',
                    padding: `${spacing.sm} ${spacing.md}`,
                    borderBottom: `1px solid ${borderColor}`,
                    alignItems: 'center',
                    fontSize: fontSize.sm,
                    cursor: 'pointer',
                  }}
                  onClick={() => selectMatter(matter)}
                >
                  <span style={{ fontWeight: 500, color: textColor }}>{matter.name}</span>
                  <span style={{ color: mutedColor, fontFamily: 'monospace', fontSize: fontSize.xs }}>
                    {matter.id}
                  </span>
                  {showAll && (
                    <span style={{ color: mutedColor, fontFamily: 'monospace', fontSize: fontSize.xs }}>
                      {clientCode(matter.id_client)}
                    </span>
                  )}
                  <span style={{ color: mutedColor, fontFamily: 'monospace', fontSize: fontSize.xs }}>
                    {matter.mattercode || '—'}
                  </span>
                  <Flex gap="xs" justify="end" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    <RoleGate minRole={GATES.hierarchyWrite}>
                      <Button variant="ghost" size="sm" onClick={() => setEditingMatter(matter)}>Edit</Button>
                      <Button variant="ghost" size="sm" color="error" onClick={() => { setDeleteTarget(matter); setDeleteBlockedMsg(''); }}>Delete</Button>
                    </RoleGate>
                  </Flex>
                </div>
              ))}
            </div>
          ) : (
            <Flex direction="column" align="center" style={{ padding: spacing.lg }}>
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>{search ? 'No matters match your search' : showAll ? 'No matters found' : 'No matters found for this client'}</span>
            </Flex>
          )}
        </Card.Body>
      </Card>

      {/* Add Modal */}
      {showAddModal && (
        <MatterFormModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSave={handleSaveNew}
          initialName=""
          initialCode=""
          title="Add Matter"
        />
      )}

      {/* Edit Modal */}
      {editingMatter && (
        <MatterFormModal
          isOpen={!!editingMatter}
          onClose={() => setEditingMatter(null)}
          onSave={handleSaveEdit}
          initialName={editingMatter.name}
          initialCode={editingMatter.mattercode || ''}
          title="Edit Matter"
        />
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Matter"
        message={deleteBlockedMsg || `Are you sure you want to delete "${deleteTarget?.name}"?`}
        confirmLabel={deleteBlockedMsg ? 'OK' : 'Delete'}
        variant={deleteBlockedMsg ? 'warning' : 'danger'}
        onConfirm={deleteBlockedMsg ? () => setDeleteTarget(null) : handleDeleteConfirm}
        onCancel={() => { setDeleteTarget(null); setDeleteBlockedMsg(''); }}
      />
    </Stack>
  );
}
